import { useCallback } from "react";

import type { DocumentNode } from "graphql";

import { useDisplayCurrency } from "@/features/display-currency/useDisplayCurrency";
import {
  type MutationHookOptions,
  type OperationVariables,
  useMutation,
} from "@apollo/client";

/** Keep the requested FX snapshot with the actual mutation, including cache updates. */
export const useDisplayCurrencyMutation = <
  TData,
  TVariables extends OperationVariables,
>(
  document: DocumentNode,
  options?: MutationHookOptions<TData, TVariables>
) => {
  const displayCurrency = useDisplayCurrency();
  const [mutate, state] = useMutation<TData, TVariables>(document, options);
  const mutateWithCurrency: typeof mutate = useCallback(
    call =>
      mutate({
        ...call,
        variables: {
          ...options?.variables,
          ...call?.variables,
          displayCurrency,
        } as unknown as TVariables,
      }),
    [mutate, options?.variables, displayCurrency]
  );
  return [mutateWithCurrency, state] as const;
};
